import { selectStorageAccount, captureAccount } from "../services/accountStorage";
import queryClient from "../services/reactQuery";
import React, { createContext, useEffect, useState } from "react";
import {
  clearForLogout,
  getLocalData,
  removeKey,
  storeLocalData,
} from "../services/storage";
import { User } from "../types/user";
import Toast from "react-native-toast-message";
import * as SplashScreen from "expo-splash-screen";
import middlewareAPI from "../services/middleware";
import openhouseAPI from "../services/openhouse";
import moment from "moment";
import { Platform } from "react-native";

interface GlobalContextProps {
  signed: boolean;
  isTablet: boolean;
  setIsTablet: (isTablet: boolean) => void;
  error: string | null;
  login: (
    loginInfo: { email: string; password: string },
    keepMeLoggedIn: boolean
  ) => void;
  user: User | null;
  token: string | null;
  logout: () => void;
  defaultForms: any;
}

const GlobalContext = createContext({} as GlobalContextProps);
export default GlobalContext;

export const GlobalProvider: React.FC = ({ children }) => {
  const [signed, setSigned] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isTablet, setIsTablet] = useState(false);
  const [keepMeLoggedIn, setKeepMeLoggedIn] = useState(false);
  const [defaultForms, setDefaultForms] = useState<any>(null);

  React.useEffect(() => {
    checkForUser();
  }, []);

  const checkForUser = async () => {
    if (Platform.OS === "web") {
      const urlParams = new URLSearchParams(window.location.search);
      const checkInKey = urlParams.get("id");
      if (checkInKey) return;
    }

    const localUser = await getLocalData("@user");
    const localToken = await getLocalData("@token");
    if (localUser && localToken) {
      await selectStorageAccount(localUser);
      setUser(localUser);
      setToken(localToken);
      setSigned(true);
      getForm();
      setTimeout(async () => {
        await SplashScreen.hideAsync();
      }, 500);
    }
  };

  const _getMlsId = async (agentId: string) => {
    try {
      const _user = await middlewareAPI.post("blueroofApi/agentProfile", {
        agentId,
      });

      const mlsIds = _user?.data?.data?.sites[0]?.mls
        ?.map((mls: any) => mls.id.toString())
        .filter(
          (value: any, index: any, self: any) => self.indexOf(value) === index
        );

      return mlsIds;
    } catch (e) {
      console.log("Error getting MLS ID", e);
      return []; // Return an empty array or handle the error appropriately
    }
  };

  const getForm = async () => {
    const assertAccount = captureAccount();
    openhouseAPI.post("/form/list", { page: 1, perPage: 10 }).then((res) => {
      assertAccount();
      res.data.data.forms.forEach((form: any) => {
        storeLocalData(`@form+${form.uuid}`, form);
        openhouseAPI.get(`/form/questions/${form.uuid}`).then((questions) => {
          assertAccount();
          storeLocalData(`@formQuestions+${form.uuid}`, questions.data.data);
          questions.data.data.forEach((question: any) => {
            openhouseAPI
              .get(`/form/question/answers/${question.uuid}`)
              .then((answer) => {
                assertAccount();
                storeLocalData(
                  `@formQuestionAnswers+${question.uuid}`,
                  answer.data
                );
              });
          });
        });
      });
      setDefaultForms(res.data.data.forms);
    });
  };

  const login = async (loginInfo: { email: string; password: string }) => {
    openhouseAPI
      .post("/auth/crmLogin", loginInfo)
      .then(async (res) => {
        let person = res.data.person;
        let user = res.data.openHouseUser;
        person.openHouseUuid = user.uuid;
        person.organizatonUuid = user.organization_uuid;
        await selectStorageAccount({ ...user, ...person });
        queryClient.clear();
        const timestamp30Days = moment().add(30, "days").toISOString();
        await storeLocalData("@timestamp30Days", timestamp30Days);
        setLoginError(null);
        await storeLocalData("@token", res.data.token);
        setToken(res.data.token);

        const mlsIds = await _getMlsId(person.id.toString());
        let orgInfo;
        try {
          orgInfo = await middlewareAPI.get(
            `brivityPeople/placePartnerInformation`
          );
        } catch (error) {
          console.log("Error getting organization name:", error);
          orgInfo = null;
        }
        person.mlsIds = mlsIds;
        let userData = {
          ...user,
          organization_info: { ...orgInfo?.data },
          ...person,
        };
        await storeLocalData("@user", userData);
        setUser(userData);
        setSigned(true);
        getForm();
        setTimeout(async () => {
          await SplashScreen.hideAsync();
        }, 500);
      })
      .catch((e) => {
        setLoginError("error");
        Toast.show({
          type: "error",
          text1: "Error logging in, please try again.",
        });
        logout();
      });
  };

  const logout = async () => {
    if (!keepMeLoggedIn) {
      // await SecureStore.deleteItemAsync("authentication");
    }
    await selectStorageAccount(null);
    await queryClient.cancelQueries();
    queryClient.clear();
    setToken(null);
    setDefaultForms(null);
    await removeKey("@user");
    await removeKey("@token");
    setSigned(false);
    setUser(null);
    await clearForLogout();
    await SplashScreen.hideAsync();

    if (Platform.OS === "web") {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const getTimeStamp = async () => {
    const timestamp = await getLocalData("@timestamp30Days");
    if (timestamp) {
      if (moment().isAfter(timestamp)) {
        logout();
      }
    }
  };

  useEffect(() => {
    getTimeStamp();
  }, []);

  return (
    <GlobalContext.Provider
      value={{
        signed,
        isTablet,
        setIsTablet,
        error: loginError,
        login,
        user,
        token,
        logout,
        defaultForms,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
